import type { ReactNode } from "react";
import { Archive, Building2, LogOut, MessageCircle } from "lucide-react";
import { AccountPayersBox, NotifyPayersButton } from "@/components/billing/AccountPayers";
import { CLOSED_ADMINS_TITLE, closedAccountCopy, closedAccountMailtoHref, closedSupportMessage } from "@/lib/account-closure";
import { whatsappHref, type OrgPayer } from "@/lib/account-resolve";
import type { ClosedReason } from "@/lib/account-standing";

export interface ClosedAccountScreenProps {
  reason: ClosedReason;
  orgName: string | null;
  closedAt: string | null;
  isAdmin: boolean;
  admins?: OrgPayer[];
  adminsLoading?: boolean;
  whatsappNumber: string | null | undefined;
  downloadAction?: ReactNode;
  onSwitchWorkspace?: () => void;
  onSignOut: () => void;
}

const SECONDARY =
  "flex-1 flex min-h-11 items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition-colors";

export function ClosedAccountScreen({
  reason,
  orgName,
  closedAt,
  isAdmin,
  admins = [],
  adminsLoading = false,
  whatsappNumber,
  downloadAction,
  onSwitchWorkspace,
  onSignOut,
}: ClosedAccountScreenProps) {
  const copy = closedAccountCopy({ reason, orgName, closedAt, isAdmin });
  const support = whatsappHref(whatsappNumber, closedSupportMessage(orgName));
  const downloaders = isAdmin ? [] : admins.filter((admin) => admin.role === "admin");
  const showDownloaders = !isAdmin && (adminsLoading || downloaders.length > 0);
  const mailto = isAdmin ? null : closedAccountMailtoHref(downloaders, orgName, closedAt);

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-8" data-testid="closed-account-screen">
      <div className="flex w-full max-w-md flex-col items-center gap-5 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Archive className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold font-display text-foreground">{copy.title}</h1>
          <p className="text-sm text-muted-foreground">{copy.reason}</p>
        </div>

        <section className="w-full rounded-xl border border-border bg-card p-4 text-left space-y-3" aria-label="Tus datos">
          <p className="text-sm text-foreground">{copy.retention}</p>
          <p className="text-sm text-muted-foreground">{copy.downloadHint}</p>
          {isAdmin && downloadAction}
          {showDownloaders && (
            <AccountPayersBox payers={downloaders} loading={adminsLoading} title={CLOSED_ADMINS_TITLE} className="bg-background" />
          )}
          {mailto && <NotifyPayersButton href={mailto} className="min-h-11 w-full" />}
        </section>

        {support && (
          <a
            href={support}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> Escribir a soporte por WhatsApp
          </a>
        )}

        <div className="flex w-full flex-col gap-2 sm:flex-row">
          {onSwitchWorkspace && (
            <button type="button" onClick={onSwitchWorkspace} className={SECONDARY}>
              <Building2 className="h-4 w-4" aria-hidden="true" /> Cambiar workspace
            </button>
          )}
          <button type="button" onClick={onSignOut} className={SECONDARY}>
            <LogOut className="h-4 w-4" aria-hidden="true" /> Cerrar sesión
          </button>
        </div>
      </div>
    </main>
  );
}
